import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withTheme } from 'styled-components';
import { useQuery } from 'react-query';
import { useRouter } from 'next/router';
import { useWindowScroll } from 'react-use';
import FocusLock from 'react-focus-lock';

import {
	BaseLink,
	HamburgerButton,
	SearchButton,
	Logo,
	HamburgerMenu,
} from 'shared/components';
import {
	HeaderWrapper,
	MenuContainer,
	HeaderLinkContainer,
} from './Header.styles';

import { getPageByUrl } from 'api';
import { ColumnContainer } from 'shared/layout';

import { removeQueryString } from 'shared/helpers';
import HeaderSearch from './HeaderSearch';
import { useOuterClick } from 'shared/hooks';
import { useMediaQuery } from 'shared/hooks/useMediaQuery';

const Header = ({
	theme = {},
	Items = [],
	NavigationLabel = '',
	SearchButtonLabel = '',
	MenuButtonLabel = '',
	MenuButtonCloseLabel = '',
	NavigationLinks = [],
	SearchPageUrl = '',
	SearchPlaceholder = '',
	EnglishSiteLabel = '',
	EnglishSiteUrl = '',
}) => {
	const { asPath } = useRouter();
	const urlToGet = removeQueryString(asPath);
	const { data: page } = useQuery(urlToGet, () => getPageByUrl(urlToGet));
	const [openMenu, setOpenMenu] = useState(false);
	const [isSearchOpen, setIsSearchOpen] = useState(false);
	const isLargeUp = useMediaQuery(
		`(min-width: ${theme.breakpoints.lg})`,
		true,
	);
	const offset = useWindowScroll().y;

	const searchButtonRef = useRef();
	const searchContainerRef = useRef();

	const innerRef = useOuterClick((e) => {
		if (
			searchContainerRef.current &&
			searchContainerRef.current.contains(e.target)
		) {
			return;
		}
		if (isSearchOpen) setIsSearchOpen(false);
	});

	const handleSearchButtonClick = useCallback(
		(e) => {
			e.preventDefault();
			if (openMenu) setOpenMenu(false);
			setIsSearchOpen((prev) => !prev);
		},
		[openMenu],
	);

	const handleMenuButtonClick = (e) => {
		e.preventDefault();
		if (isSearchOpen) setIsSearchOpen(false);
		setOpenMenu(!openMenu);
	};

	useEffect(() => {
		const handleKeyDown = (e) => {
			if (e.key === 'Escape' && isSearchOpen) {
				setIsSearchOpen(false);
				setTimeout(() => {
					searchButtonRef.current?.focus();
				}, 100);
			}
		};

		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [isSearchOpen]);

	return (
		<HeaderWrapper
			offset={
				page?.navigationWhiteBackground
					? 'true'
					: (offset !== 0).toString()
			}
			aria-label={NavigationLabel}
		>
			{isSearchOpen && (
				<HeaderSearch
					ref={innerRef}
					SearchPageUrl={SearchPageUrl}
					isSearchOpen={isSearchOpen}
					SearchPlaceholder={SearchPlaceholder}
				/>
			)}
			<ColumnContainer
				max-width={theme.spacings.siteWidth}
				margin="0 auto"
				flex-direction="row"
				flex-wrap="nowrap"
				justify-content="space-between"
				align-items="center"
				height="100%"
				noGutters
			>
				<Logo
					padding={{ base: theme.spacings[4], lg: 0 }}
					type="header-logo"
					color={
						isSearchOpen
							? theme.colors.white
							: theme.colors.secondary
					}
				/>
				{isLargeUp && !!Items.length && (
					<HeaderLinkContainer aria-label="Ingångslänkar">
						<ul
							style={{
								display: 'flex',
								listStyle: 'none',
								margin: 0,
								padding: 0,
							}}
						>
							{Items.map((item, ix) => {
								return (
									<li key={item.Url}>
										<BaseLink
											display={{
												base: 'none',
												lg: 'inline',
											}}
											key={ix}
											margin={`0 ${theme.spacings[5]}`}
											href={item.Url}
										>
											{item.Name}
										</BaseLink>
									</li>
								);
							})}
						</ul>
					</HeaderLinkContainer>
				)}
				<MenuContainer ref={searchContainerRef}>
					<SearchButton
						ref={searchButtonRef}
						title={SearchButtonLabel}
						isOpen={isSearchOpen}
						margin-right={theme.spacings[10]}
						className="white-tab"
						tabIndex={0}
						onClick={(e) => handleSearchButtonClick(e)}
						onKeyDown={(e) => {
							if (e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								handleSearchButtonClick(e);
							}
						}}
					/>
					<FocusLock
						group="hamburgerMenu"
						className="zindex"
						disabled={!openMenu}
					>
						<HamburgerButton
							title={MenuButtonLabel}
							closeTitle={MenuButtonCloseLabel}
							isOpen={openMenu}
							handleMenuClick={(e) => handleMenuButtonClick(e)}
							className="white-tab"
						/>
					</FocusLock>
				</MenuContainer>
			</ColumnContainer>
			<HamburgerMenu
				isOpen={openMenu}
				links={NavigationLinks}
				handleMenuClick={() => setOpenMenu(!openMenu)}
				{...{ EnglishSiteUrl, EnglishSiteLabel }}
			/>
		</HeaderWrapper>
	);
};

Header.propTypes = {
	Items: PropTypes.array,
	NavigationLabel: PropTypes.string,
	SearchButtonLabel: PropTypes.string,
	MenuButtonLabel: PropTypes.string,
	SearchPageUrl: PropTypes.string,
	SearchPlaceholder: PropTypes.string,
	EnglishSiteUrl: PropTypes.string,
	EnglishSiteLabel: PropTypes.string,
};

export default withTheme(Header);
